import Modal from '@components/v2/ui/Modal/Modal';
import {} from '@mui/material';
import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { fetcher } from '../../components/Hooks/useListActions';
import EmphasisCatalogSection from '../../components/Site/EmphasisCatalogSection';
import EmphasisMediasSectionLoading from '../../components/Site/EmphasisMediasSectionLoading';
import { SectionProps } from '../../components/Site/Section';
import LangEnum from '../../lang/LangEnum';
import snackbarStore from '../../stores/Snackbar.store';
import { Catalog, EmphasisCatalog, EmphasisCatalogSimple } from '../catalogs/catalog.types';
import { useAuthContext } from '../login/AuthContext';
import { useTranslation } from '../translation/TranslationContext';
import EmphasisCatalogEdit from './EmphasisCatalogEdit';
import { brandCatalogShowcaseRepository } from './brandCatalogShowcase.repository';

type BrandEmphasisCatalogProps = {
  sectionProps: Partial<SectionProps>;
  brandId: string;
  enableLink: boolean;
};

const catalogsFetcher = fetcher<Catalog>('/catalogs');

const BrandEmphasisCatalog = ({ sectionProps, brandId, enableLink }: BrandEmphasisCatalogProps) => {
  const { loggedin } = useAuthContext();
  const { handleMessage } = snackbarStore;
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [emphasisCatalogs, setEmphasisCatalogs] = useState<EmphasisCatalog[]>([]);
  const [editing, setEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const { translate } = useTranslation();

  const loadBrandCatalogs = useCallback(async () => {
    const catalogsResponse = await catalogsFetcher({
      page: 0,
      count: 999,
    });
    if (catalogsResponse.tag === 'success') {
      setCatalogs(catalogsResponse.data);
    }
  }, [setCatalogs]);

  const loadBrandEmphasisCatalogs = useCallback(async () => {
    setLoading(true);
    const catalogsResponse = await brandCatalogShowcaseRepository(brandId, loggedin).list();
    if (catalogsResponse.ok) {
      setEmphasisCatalogs(catalogsResponse?.data?.data || []);
    }
    setLoading(false);
  }, [brandId, loggedin]);

  useEffect(() => {
    const emphasisCatalogsTimeout = setTimeout(loadBrandEmphasisCatalogs, 500);

    return () => clearTimeout(emphasisCatalogsTimeout);
  }, [brandId, loadBrandEmphasisCatalogs]);

  useEffect(() => {
    let catalogsTimeout: NodeJS.Timeout | null = null;
    if (brandId && editing) {
      catalogsTimeout = setTimeout(loadBrandCatalogs, 500);
    } else {
      setCatalogs([]);
    }

    return () => {
      if (catalogsTimeout) {
        clearTimeout(catalogsTimeout);
      }
    };
  }, [editing, brandId, loadBrandCatalogs]);

  const handleSaveEmphasisCatalog = async (emphasisCatalog: EmphasisCatalog[]) => {
    setLoading(true);
    const emphasisCatalogsSimple: EmphasisCatalogSimple[] = emphasisCatalog.map((ec, index) => ({
      catalogId: ec.catalog.id,
      position: index,
    }));

    const result =
      await brandCatalogShowcaseRepository(brandId).synchronize(emphasisCatalogsSimple);

    if (result.ok) {
      await loadBrandEmphasisCatalogs();
      setEditing(false);
    } else {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_ERROR),
      });
    }
    setLoading(false);
  };

  if (sectionProps.readOnly && emphasisCatalogs.length === 0) {
    return null;
  }

  if (loading) {
    return <EmphasisMediasSectionLoading />;
  }

  return (
    <>
      {!sectionProps.readOnly && (
        <Modal
          isOpen={editing}
          size="expanded"
          header={{ title: translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_SELECTED) }}
          isDismissable={false}
          onClose={() => setEditing(false)}
        >
          <Box>
            <EmphasisCatalogEdit
              loading={loading}
              catalogs={catalogs}
              emphasisCatalogs={emphasisCatalogs}
              onSave={handleSaveEmphasisCatalog}
              onCancel={() => setEditing(false)}
              onChangeEmphasisCatalogs={setEmphasisCatalogs}
            />
          </Box>
        </Modal>
      )}
      <EmphasisCatalogSection
        sectionProps={{ ...sectionProps, onEdit: () => setEditing(true) }}
        emphasisCatalogs={emphasisCatalogs}
        enableLink={enableLink}
      />
    </>
  );
};

export default BrandEmphasisCatalog;
